/**
 * Los iconos de la web, dibujados a mano.
 *
 * Mismo criterio que en la app de escritorio: son pocos, pesan nada, y así el
 * trazo y el color quedan atados a los tokens en vez de a las decisiones de un
 * paquete de terceros. Todos heredan `currentColor`.
 */

export type NombreIcono =
  | 'inicio'
  | 'torneos'
  | 'historial'
  | 'medalla'
  | 'mesa'
  | 'salir'
  | 'aviso'
  | 'sede'
  | 'calendario'
  | 'flecha'
  | 'reloj'
  | 'buscar'
  | 'refrescar'
  | 'gente'
  | 'vivo'
  | 'atras'
  | 'entrar'
  | 'sol'
  | 'luna'
  | 'persona'
  | 'candado'
  | 'libreta'
  | 'mas'

const TRAZOS: Record<NombreIcono, React.ReactNode> = {
  // Casa — la pantalla de hoy
  inicio: (
    <>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M5.8 9.8V20h12.4V9.8" />
      <path d="M9.8 20v-5.4h4.4V20" />
    </>
  ),
  // Trofeo — la lista de sus torneos. Es el mismo de la app: el jugador y el
  // organizador miran los mismos torneos, y el dibujo no tiene por qué cambiar.
  torneos: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4.5a2.5 2.5 0 0 0 2.5 2.5M17 6h2.5a2.5 2.5 0 0 1-2.5 2.5" />
      <path d="M12 14v4M8.5 20h7" />
    </>
  ),
  // Reloj con la flecha hacia atrás — lo que ya jugó
  historial: (
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3.3 4.6v4.2h4.2" />
      <path d="M12 7.8V12l3 1.8" />
    </>
  ),
  // Medalla — el podio del historial
  medalla: (
    <>
      <path d="M7.2 2h2.4l2.5 6.3-2.3 1.2L7.2 2Z" fill="currentColor" />
      <path d="M16.8 2h-2.4l-2.5 6.3 2.3 1.2L16.8 2Z" fill="currentColor" />
      <circle cx="12" cy="15.2" r="6.3" />
      <circle cx="12" cy="15.2" r="2.5" fill="currentColor" stroke="none" />
    </>
  ),
  // Una mesa con sus cuatro sillas — dónde le toca sentarse
  mesa: (
    <>
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3" />
    </>
  ),
  // Puerta con la flecha hacia afuera — cerrar la sesión
  salir: (
    <>
      <path d="M14 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5H14" />
      <path d="M17 8.5 20.5 12 17 15.5M20 12h-9" />
    </>
  ),
  // Triángulo de advertencia — el aviso de modo demostración
  aviso: (
    <>
      <path d="M12 4.5 21 19.5H3L12 4.5Z" />
      <path d="M12 10v4M12 16.8v.2" />
    </>
  ),
  // Chincheta — dónde se juega
  sede: (
    <>
      <path d="M12 21s6.5-6 6.5-11a6.5 6.5 0 1 0-13 0C5.5 15 12 21 12 21Z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  // Almanaque — cuándo se juega
  calendario: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </>
  ),
  // Chevron a la derecha — entrar a un torneo desde la lista
  flecha: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  // Lupa — buscar a alguien entre las cien mesas de la ronda
  buscar: (
    <>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.4 15.4 4.3 4.3" />
    </>
  ),
  // Flecha en círculo — volver a pedir los datos mientras la ronda se juega
  refrescar: (
    <>
      <path d="M20 12a8 8 0 1 1-2.4-5.7" />
      <path d="M20.2 3.4v4.2H16" />
    </>
  ),
  // Dos siluetas — cuántos juegan
  gente: (
    <>
      <circle cx="9.2" cy="8.4" r="3.4" />
      <path d="M3.2 20c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4" />
      <path d="M16 5.4a3.4 3.4 0 0 1 0 6.5M17.2 14.9c2.2.5 3.6 2.4 3.6 5.1" />
    </>
  ),
  // Un punto con dos ondas a cada lado: la vitrina, lo que se transmite en vivo
  vivo: (
    <>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      <path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 8.2a5.4 5.4 0 0 1 0 7.6" />
      <path d="M5.3 5.3a9.5 9.5 0 0 0 0 13.4M18.7 5.3a9.5 9.5 0 0 1 0 13.4" />
    </>
  ),
  // Chevron a la izquierda: volver de un torneo a la vitrina
  atras: <path d="m14.5 5.5-6.5 6.5 6.5 6.5" />,
  // Puerta con la flecha hacia adentro: ingresar con la cuenta
  entrar: (
    <>
      <path d="M10 4.5h7.5A1.5 1.5 0 0 1 19 6v12a1.5 1.5 0 0 1-1.5 1.5H10" />
      <path d="M11 8.5 14.5 12 11 15.5M14 12H4" />
    </>
  ),
  // Reloj — la ronda que se está jugando ahora
  reloj: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.2 1.9" />
    </>
  ),
  // Sol: pasar al tema claro
  sol: (
    <>
      <circle cx="12" cy="12" r="3.8" />
      <path d="M12 3v1.8M12 19.2V21M3 12h1.8M19.2 12H21M5.6 5.6l1.3 1.3M17.1 17.1l1.3 1.3M5.6 18.4l1.3-1.3M17.1 6.9l1.3-1.3" />
    </>
  ),
  // Una persona: el campo del usuario en el ingreso
  persona: (
    <>
      <circle cx="12" cy="8.2" r="3.7" />
      <path d="M4.8 20c0-3.6 3.2-6 7.2-6s7.2 2.4 7.2 6" />
    </>
  ),
  // Candado: el campo de la contraseña. El mismo dibujo que en la app.
  candado: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </>
  ),
  // Libreta con espiral y dos renglones: las partidas propias, fuera de torneo
  libreta: (
    <>
      <rect x="5.5" y="3.5" width="13" height="17" rx="2" />
      <path d="M3.8 7.5h3.4M3.8 12h3.4M3.8 16.5h3.4" />
      <path d="M10.5 9h5M10.5 13h5" />
    </>
  ),
  // Cruz: empezar algo nuevo
  mas: <path d="M12 5v14M5 12h14" />,
  // Luna: pasar al tema oscuro
  luna: <path d="M19.5 14.2A7.8 7.8 0 0 1 9.8 4.5a7.8 7.8 0 1 0 9.7 9.7Z" />,
}

export function Icono({ nombre, tamano = 20 }: { nombre: NombreIcono; tamano?: number }) {
  return (
    <svg
      width={tamano}
      height={tamano}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {TRAZOS[nombre]}
    </svg>
  )
}
